import * as THREE from "three";
import { ShadowProxies } from "./shadowProxies";
import { BoneAtlas } from "./boneAtlas";

/**
 * Rigid-part rigs drawn as one skinned mesh per material instead of one mesh per part.
 *
 * Every HQ model is a hierarchy of rigid parts animated by node transforms (art/hq/README.md "Animation"): a raider
 * is nine meshes, an archer tower twenty, the fortress fifty-six — all sharing one or two materials. Each part was its
 * own draw call, twice over for the shadow pass, and a phone spends its frame submitting them: a battle with seven
 * towers and thirty units was ~1,200 draws, ~7 ms of JavaScript in `WebGLRenderer.render` on a desktop CPU alone.
 *
 * Here the parts that share a material become one `SkinnedMesh` whose "bones" are the part nodes themselves, one bone
 * per vertex with weight 1. The clips still animate the same named nodes, `hqNode` still finds them, and the vertex
 * shader places each part with its node's world matrix — the picture is unchanged, the draws are one per material.
 *
 * The maths: in attached bind mode three.js draws `modelMatrix · bindMatrixInverse · bone.matrixWorld ·
 * boneInverse · bindMatrix · p`. With `bindMatrix` and every `boneInverse` the identity, and `bindMatrixInverse`
 * = inverse(skinned mesh's matrixWorld), that is `bone.matrixWorld · p` — exactly what the part mesh drew, `p` being
 * the part's own vertex (kept in its original, often quantized, format).
 */
export class RigidRig {
  /**
   * Families merged at load: units, towers, the fortress and its skins' companions (fortress_*), projectiles, the portal, and the trees and bushes (a tree is
   * five meshes, a bush four, each drawn again in the shadow pass). Other props stay plain: they are one or two meshes,
   * and some double as the ground-detail fallback, which instances raw part geometry (detail.ts).
   */
  static readonly Families = /^(tower_|base_|fortress_|enemy_|boss_|troop_|proj_|spawn_portal|p_(tree|bush|cypress|dead_willow|ash_tree))/;

  /**
   * Rest-pose bounds grown by this factor for frustum culling: clips swing parts past the rest pose (a raider's
   * stride, a mortar's recoil, the fortress's charge), and a unit at the screen edge must not pop out while it does.
   */
  static readonly CullPad = 1.25;

  private static installed = false;

  /**
   * The shadow pass's depth material for every rigid rig. three.js shares one depth material across all casters, so
   * a skinned caster between two plain ones switched its program twice; skinned casters get their own copy of the
   * stock one (same RGBA depth packing) and stay on theirs.
   */
  static readonly depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });

  /**
   * Makes the stock skinning chunks cheap for one-bone vertices. three.js reads four bone matrices per vertex —
   * sixteen texel fetches — whatever the weights; a rigid part's vertex needs one. The other three are only fetched
   * when the first weight is below 1, so real multi-bone skins still work.
   */
  static installSkinning(): void {
    if (RigidRig.installed) return;
    RigidRig.installed = true;
    BoneAtlas.installShader();
    THREE.ShaderChunk.skinbase_vertex = `#ifdef USE_SKINNING
	mat4 boneMatX = getBoneMatrix( skinIndex.x );
	mat4 boneMatY = mat4( 0.0 );
	mat4 boneMatZ = mat4( 0.0 );
	mat4 boneMatW = mat4( 0.0 );
	if ( skinWeight.x < 1.0 ) {
		boneMatY = getBoneMatrix( skinIndex.y );
		boneMatZ = getBoneMatrix( skinIndex.z );
		boneMatW = getBoneMatrix( skinIndex.w );
	}
#endif`;
  }

  /**
   * Merges a loaded model's parts in place (the library's template; clones go through `clone`). Returns how many
   * draw calls one instance saves; 0 when there was nothing to merge.
   */
  static merge(root: THREE.Object3D): number {
    root.updateMatrixWorld(true);
    const groups = new Map<string, THREE.Mesh[]>();
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!RigidRig.mergeable(mesh)) return;
      const key = `${(mesh.material as THREE.Material).uuid}|${mesh.castShadow}|${mesh.receiveShadow}|${mesh.renderOrder}|${RigidRig.layout(mesh.geometry)}`;
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(mesh);
    });
    // Once a model has a merged group, every group becomes skinned — singletons too. A material drawn by both a plain
    // mesh and a skinned one (a raider's body casts the shadow, its kit does not) has two programs, and three.js
    // re-derives the program on every switch between them: that was most of the win given back.
    const merged = [...groups.values()];
    if (!merged.some((list) => list.length > 1)) return 0;
    const bones: THREE.Bone[] = [];
    const skins: THREE.SkinnedMesh[] = [];
    let saved = 0;
    for (const parts of merged) {
      const first = bones.length;
      const bonesHere = parts.map((m) => RigidRig.toBone(m));
      bones.push(...bonesHere);
      const skin = new THREE.SkinnedMesh(RigidRig.mergeGeometry(parts, first), parts[0].material);
      skin.name = `${parts[0].name}_rigid`;
      skin.castShadow = parts[0].castShadow;
      skin.receiveShadow = parts[0].receiveShadow;
      skin.renderOrder = parts[0].renderOrder;
      skin.customDepthMaterial = RigidRig.depth;
      skins.push(skin);
      saved += parts.length - 1;
    }
    const skeleton = new THREE.Skeleton(bones, bones.map(() => new THREE.Matrix4()));
    const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();
    const rest = new THREE.Box3();
    for (const b of bones) b.updateWorldMatrix(true, false);
    for (const skin of skins) {
      root.add(skin);                                   // last, so a traversal meets the bones before the mesh
      skin.bind(skeleton, new THREE.Matrix4());
      skin.updateMatrixWorld(true);
    }
    // Bounds from each part's own box, as Box3.setFromObject measured the part meshes: the same rest box as before,
    // without skinning every vertex of a 300k-triangle fortress at load.
    for (const skin of skins) {
      const box = new THREE.Box3();
      const index = skin.geometry.getAttribute("skinIndex");
      const seen = new Set<number>();
      for (let i = 0; i < index.count; i++) seen.add(index.getX(i));
      for (const b of seen) {
        const part = RigidRig.partBox.get(bones[b]);
        if (part) box.union(part.clone().applyMatrix4(new THREE.Matrix4().multiplyMatrices(toRoot, bones[b].matrixWorld)));
      }
      skin.boundingBox = box;
      rest.union(box);
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      sphere.radius *= RigidRig.CullPad;
      skin.boundingSphere = sphere;
    }
    return saved;
  }

  /**
   * A fresh instance of a merged template: the node tree cloned, each skinned mesh re-bound to the clone's nodes, and
   * its skeleton drawn from the shared bone atlas (boneAtlas.ts) rather than a texture of its own.
   */
  static clone(src: THREE.Object3D): THREE.Object3D {
    const inst = src.clone(true);
    const map = new Map<THREE.Object3D, THREE.Object3D>();
    RigidRig.parallel(src, inst, map);
    const skeletons = new Map<THREE.Skeleton, THREE.Skeleton>();
    const skinsOf = new Map<THREE.Skeleton, THREE.SkinnedMesh[]>();
    inst.traverse((o) => {
      const skin = o as THREE.SkinnedMesh;
      if (!skin.isSkinnedMesh) return;
      const from = skin.skeleton;
      let s = skeletons.get(from);
      if (!s) {
        s = new THREE.Skeleton(from.bones.map((b) => map.get(b) as THREE.Bone), from.boneInverses);
        skeletons.set(from, s);
      }
      skin.bind(s, skin.bindMatrix);
      skin.customDepthMaterial = RigidRig.depth;          // Object3D.copy does not carry it
      (skinsOf.get(s) ?? skinsOf.set(s, []).get(s)!).push(skin);
    });
    for (const [s, skins] of skinsOf) BoneAtlas.adopt(s, skins);
    return inst;
  }

  /**
   * Gives back the bone-atlas slots of a clone that is being thrown away, forgets its shadow proxies
   * (shadowProxies.ts), and frees the buffers an instance added to it for itself (`userData.ownsGeometry`, e.g. the
   * fortress's chimney smoke). The clone's other geometry is the library's, shared by every copy, and stays.
   */
  static dispose(root: THREE.Object3D): void {
    const done = new Set<THREE.Skeleton>();
    ShadowProxies.removeUnder(root);
    root.traverse((o) => {
      if (o.userData.ownsGeometry === true) (o as THREE.Mesh).geometry?.dispose();
      const s = (o as THREE.SkinnedMesh).skeleton;
      if (!(o as THREE.SkinnedMesh).isSkinnedMesh || !s || done.has(s)) return;
      done.add(s);
      BoneAtlas.release(s);
    });
  }

  /**
   * Bakes a rigid rig's vertices where its bones stand now, as plain float attributes in `to`'s space (for
   * staticBatch.ts, which merges scenery that never animates). Returns null for anything that is not a rigid rig.
   */
  static bake(skin: THREE.SkinnedMesh, to: THREE.Matrix4, copy: (g: THREE.BufferGeometry) => THREE.BufferGeometry): THREE.BufferGeometry | null {
    const index = skin.geometry.getAttribute("skinIndex"), weight = skin.geometry.getAttribute("skinWeight");
    if (!index || !weight) return null;
    const geo = copy(skin.geometry);
    geo.deleteAttribute("skinIndex");
    geo.deleteAttribute("skinWeight");
    const pos = geo.getAttribute("position") as THREE.BufferAttribute, nrm = geo.getAttribute("normal") as THREE.BufferAttribute | undefined;
    const bones = skin.skeleton.bones, m = new THREE.Matrix4(), n = new THREE.Matrix3(), v = new THREE.Vector3();
    let current = -1;
    for (let i = 0; i < pos.count; i++) {
      if (weight.getX(i) < 1) return null;                // a real skin, not a rigid rig: leave it to the caller
      const b = index.getX(i);
      if (b !== current) { current = b; m.multiplyMatrices(to, bones[b].matrixWorld); n.getNormalMatrix(m); }
      v.fromBufferAttribute(pos, i).applyMatrix4(m); pos.setXYZ(i, v.x, v.y, v.z);
      if (nrm) { v.fromBufferAttribute(nrm, i).applyMatrix3(n).normalize(); nrm.setXYZ(i, v.x, v.y, v.z); }
    }
    return geo;
  }

  /** Part boxes in the part's own space, kept for the bounds pass. */
  private static readonly partBox = new WeakMap<THREE.Object3D, THREE.Box3>();

  private static mergeable(mesh: THREE.Mesh): boolean {
    if (!mesh.isMesh || (mesh as THREE.SkinnedMesh).isSkinnedMesh || (mesh as THREE.InstancedMesh).isInstancedMesh) return false;
    if (Array.isArray(mesh.material) || mesh.children.length > 0 || !mesh.visible) return false;
    const g = mesh.geometry;
    if (!g.index || Object.keys(g.morphAttributes).length > 0 || g.drawRange.start !== 0 || Number.isFinite(g.drawRange.count)) return false;
    return !!g.getAttribute("position");
  }

  /** Attribute names, sizes, storage types and normalisation: parts merge only when their buffers line up. */
  private static layout(g: THREE.BufferGeometry): string {
    return Object.keys(g.attributes).sort().map((k) => {
      const a = g.getAttribute(k);
      const arr = (a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute ? (a as THREE.InterleavedBufferAttribute).data.array : (a as THREE.BufferAttribute).array;
      return `${k}:${a.itemSize}:${arr.constructor.name}:${a.normalized}`;
    }).join(",");
  }

  /** Swaps the part mesh for a plain node with the same name and transform; the node is the part's bone from now on. */
  private static toBone(mesh: THREE.Mesh): THREE.Bone {
    const bone = new THREE.Bone();
    bone.name = mesh.name;
    bone.position.copy(mesh.position);
    bone.quaternion.copy(mesh.quaternion);
    bone.scale.copy(mesh.scale);
    const parent = mesh.parent!;
    parent.children[parent.children.indexOf(mesh)] = bone;
    bone.parent = parent;
    mesh.parent = null;
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    RigidRig.partBox.set(bone, mesh.geometry.boundingBox!.clone());
    return bone;
  }

  /** Concatenates the parts' raw buffers and tags every vertex with its part's bone (weight 1). */
  private static mergeGeometry(parts: readonly THREE.Mesh[], firstBone: number): THREE.BufferGeometry {
    const out = new THREE.BufferGeometry();
    const total = parts.reduce((n, m) => n + m.geometry.getAttribute("position").count, 0);
    for (const name of Object.keys(parts[0].geometry.attributes)) {
      const a0 = parts[0].geometry.getAttribute(name);
      const Ctor = RigidRig.rawArray(a0).constructor as new (n: number) => THREE.TypedArray;
      const arr = new Ctor(total * a0.itemSize);
      let at = 0;
      for (const m of parts) {
        const a = m.geometry.getAttribute(name);
        RigidRig.copyRaw(a, arr, at);
        at += a.count * a.itemSize;
      }
      out.setAttribute(name, new THREE.BufferAttribute(arr, a0.itemSize, a0.normalized));
    }
    const indexCount = parts.reduce((n, m) => n + m.geometry.index!.count, 0);
    const index = total > 0xffff ? new Uint32Array(indexCount) : new Uint16Array(indexCount);
    const skinIndex = new Uint16Array(total * 4);
    const skinWeight = new Uint8Array(total * 4);
    let base = 0, at = 0;
    parts.forEach((m, p) => {
      const src = m.geometry.index!;
      for (let i = 0; i < src.count; i++) index[at++] = src.getX(i) + base;
      const n = m.geometry.getAttribute("position").count;
      for (let v = base; v < base + n; v++) { skinIndex[v * 4] = firstBone + p; skinWeight[v * 4] = 255; }
      base += n;
    });
    out.setIndex(new THREE.BufferAttribute(index, 1));
    out.setAttribute("skinIndex", new THREE.BufferAttribute(skinIndex, 4));
    out.setAttribute("skinWeight", new THREE.BufferAttribute(skinWeight, 4, true));
    return out;
  }

  private static rawArray(a: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): THREE.TypedArray {
    return (a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute ? (a as THREE.InterleavedBufferAttribute).data.array : (a as THREE.BufferAttribute).array;
  }

  /** Copies an attribute's stored values (not the normalised floats) into `dst` from `offset`. */
  private static copyRaw(a: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, dst: THREE.TypedArray, offset: number): void {
    const s = a.itemSize;
    if (!(a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute) { dst.set((a as THREE.BufferAttribute).array.subarray(0, a.count * s), offset); return; }
    const ia = a as THREE.InterleavedBufferAttribute, src = ia.data.array, stride = ia.data.stride;
    for (let i = 0; i < ia.count; i++) for (let c = 0; c < s; c++) dst[offset + i * s + c] = src[i * stride + ia.offset + c];
  }

  private static parallel(a: THREE.Object3D, b: THREE.Object3D, map: Map<THREE.Object3D, THREE.Object3D>): void {
    map.set(a, b);
    for (let i = 0; i < a.children.length; i++) RigidRig.parallel(a.children[i], b.children[i], map);
  }
}
